import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fmtMoney } from '../utils/formatters';
interface HiringFunnelProps {
  estimatedHires: number;
}
function HiringFunnel({
  estimatedHires
}: HiringFunnelProps) {
  // Fixed conversion rates (non-editable)
  const rates = {
    offeredToHired: 85,
    interviewedToOffered: 55,
    scheduledToInterviewed: 65,
    qualifiedToScheduled: 70,
    screenedToQualified: 60,
    approvedToScreened: 50,
    applicationsToApproved: 50
  };
  const applications = Math.round(estimatedHires / (rates.offeredToHired / 100) / (rates.interviewedToOffered / 100) / (rates.scheduledToInterviewed / 100) / (rates.qualifiedToScheduled / 100) / (rates.screenedToQualified / 100) / (rates.approvedToScreened / 100) / (rates.applicationsToApproved / 100));
  const approved = Math.round(applications * (rates.applicationsToApproved / 100));
  const screened = Math.round(approved * (rates.approvedToScreened / 100));
  const qualified = Math.round(screened * (rates.screenedToQualified / 100));
  const scheduled = Math.round(qualified * (rates.qualifiedToScheduled / 100));
  const interviewed = Math.round(scheduled * (rates.scheduledToInterviewed / 100));
  const offered = Math.round(interviewed * (rates.interviewedToOffered / 100));
  const hired = estimatedHires;
  const stages = [{
    label: 'Applications',
    value: applications,
    gradient: 'from-gray-300 to-gray-200',
    rate: rates.applicationsToApproved
  }, {
    label: 'Approved',
    value: approved,
    gradient: 'from-gray-400 to-gray-300',
    rate: rates.approvedToScreened
  }, {
    label: 'Screened',
    value: screened,
    gradient: 'from-blue-500 to-blue-400',
    rate: rates.screenedToQualified
  }, {
    label: 'Qualified',
    value: qualified,
    gradient: 'from-indigo-400 to-indigo-300',
    rate: rates.qualifiedToScheduled
  }, {
    label: 'Scheduled',
    value: scheduled,
    gradient: 'from-indigo-500 to-indigo-400',
    rate: rates.scheduledToInterviewed
  }, {
    label: 'Interviewed',
    value: interviewed,
    gradient: 'from-purple-400 to-purple-300',
    rate: rates.interviewedToOffered
  }, {
    label: 'Offered',
    value: offered,
    gradient: 'from-green-500 to-green-400',
    rate: rates.offeredToHired
  }, {
    label: 'Hired',
    value: hired,
    gradient: 'from-navy to-navy/90',
    rate: null
  }];
  const maxValue = stages[0].value;
  return <div className="space-y-1" data-id="element-1135">
      {stages.map((stage, index) => <div key={index} data-id="element-1136">
          <div className="flex items-center gap-2 md:gap-4" data-id="element-1137">
            <div className="w-6 md:w-8 text-right flex-shrink-0" data-id="element-1138">
              <span className="text-base md:text-xl font-bold text-navy" data-id="element-1139">
                {stage.value}
              </span>
            </div>
            <div className="w-24 md:w-48 flex-shrink-0" data-id="element-1140">
              <span className="text-xs md:text-base font-semibold text-gray-700 whitespace-nowrap" data-id="element-1141">
                {stage.label}
              </span>
            </div>
            {stage.gradient && <div className="flex-1 h-6 md:h-8 bg-gray-200 rounded-full overflow-hidden" data-id="element-1142">
                <div className={`h-full bg-gradient-to-r ${stage.gradient} transition-all duration-500 ease-out`} style={{
            width: `${stage.value / maxValue * 100}%`
          }} data-id="element-1143" />
              </div>}
            <div className="w-14 md:w-20 flex-shrink-0" data-id="element-1144" />
          </div>

          {/* Conversion rate positioned between bars */}
          {stage.rate !== null && <div className="flex items-center gap-2 md:gap-4 py-1" data-id="element-1145">
              <div className="w-6 md:w-8 flex-shrink-0" data-id="element-1146" />
              <div className="w-24 md:w-48 flex-shrink-0" data-id="element-1147" />
              <div className="flex-1 flex items-center justify-center" data-id="element-1148">
                <span className="text-xs md:text-sm font-semibold text-gray-600" data-id="element-1149">
                  ↓ {stage.rate}%
                </span>
              </div>
              <div className="w-14 md:w-20 flex-shrink-0" data-id="element-1150" />
            </div>}
        </div>)}
    </div>;
}
interface RetentionFunnelProps {
  estimatedHires: number;
}
function RetentionFunnel({
  estimatedHires
}: RetentionFunnelProps) {
  // Retention rates (editable with sliders)
  const [rates, setRates] = useState({
    hiredToLaunched: 70,
    launchedTo30d: 30,
    day30To90d: 80,
    day90To1Year: 30
  });
  const launched = Math.round(estimatedHires * (rates.hiredToLaunched / 100));
  const active30 = Math.round(launched * (rates.launchedTo30d / 100));
  const active90Plus = Math.round(active30 * (rates.day30To90d / 100));
  const active1Year = Math.round(active90Plus * (rates.day90To1Year / 100));
  const stages = [{
    label: 'Hired',
    value: estimatedHires,
    gradient: 'from-navy to-navy/90',
    rate: null,
    rateKey: null
  }, {
    label: 'Launched',
    value: launched,
    gradient: 'from-green-500 to-green-400',
    rate: rates.hiredToLaunched,
    rateKey: 'hiredToLaunched'
  }, {
    label: 'Active 30d',
    value: active30,
    gradient: 'from-blue-500 to-blue-400',
    rate: rates.launchedTo30d,
    rateKey: 'launchedTo30d'
  }, {
    label: 'Active 90d+',
    value: active90Plus,
    gradient: 'from-emerald-500 to-emerald-400',
    rate: rates.day30To90d,
    rateKey: 'day30To90d'
  }, {
    label: 'Active 1 Year',
    value: active1Year,
    gradient: 'from-purple-500 to-purple-400',
    rate: rates.day90To1Year,
    rateKey: 'day90To1Year'
  }];
  const maxValue = stages[0].value;
  const handleRateChange = (rateKey: string, value: number) => {
    setRates({
      ...rates,
      [rateKey]: value
    });
  };
  return <div className="space-y-1" data-id="element-1151">
      {stages.map((stage, index) => <div key={index} data-id="element-1152">
          {/* Slider above the bar */}
          {stage.rate !== null && stage.rateKey && <div className="mb-2" data-id="element-1153">
              <div className="ml-[calc(1.5rem+6rem)] md:ml-[calc(2rem+12rem)] mr-[3.5rem] md:mr-[5rem]" data-id="element-1154">
                <div className="flex items-center justify-between mb-1" data-id="element-1155">
                  <span className="text-xs text-gray-500" data-id="element-1156">Retention Rate</span>
                  <span className="text-sm font-bold text-green-600" data-id="element-1157">
                    {stage.rate}%
                  </span>
                </div>
                <input type="range" min="5" max="100" step="5" value={stage.rate} onChange={e => handleRateChange(stage.rateKey!, parseFloat(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-500" style={{
            background: `linear-gradient(to right, #22c55e 0%, #22c55e ${(stage.rate - 5) / (100 - 5) * 100}%, #E5E7EB ${(stage.rate - 5) / (100 - 5) * 100}%, #E5E7EB 100%)`
          }} data-id="element-1158" />
                <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-1159">
                  <span data-id="element-1160">5%</span>
                  <span data-id="element-1161">100%</span>
                </div>
              </div>
            </div>}

          {/* Bar */}
          <div className="flex items-center gap-2 md:gap-4" data-id="element-1162">
            <div className="w-6 md:w-8 text-right flex-shrink-0" data-id="element-1163">
              <span className="text-base md:text-xl font-bold text-navy" data-id="element-1164">
                {stage.value}
              </span>
            </div>
            <div className="w-24 md:w-48 flex-shrink-0" data-id="element-1165">
              <span className="text-xs md:text-base font-semibold text-gray-700 whitespace-nowrap" data-id="element-1166">
                {stage.label}
              </span>
            </div>
            <div className="flex-1 h-6 md:h-8 bg-gray-200 rounded-full overflow-hidden" data-id="element-1167">
              <div className={`h-full bg-gradient-to-r ${stage.gradient} transition-all duration-500 ease-out`} style={{
            width: `${stage.value / maxValue * 100}%`
          }} data-id="element-1168" />
            </div>
            <div className="w-14 md:w-20 flex-shrink-0" data-id="element-1169" />
          </div>

          {/* Conversion rate positioned between bars */}
          {stage.rate !== null && <div className="flex items-center gap-2 md:gap-4 py-2" data-id="element-1170">
              <div className="w-6 md:w-8 flex-shrink-0" data-id="element-1171" />
              <div className="w-24 md:w-48 flex-shrink-0" data-id="element-1172" />
              <div className="flex-1 flex items-center justify-center" data-id="element-1173">
                <span className="text-xs md:text-sm font-semibold text-green-600" data-id="element-1174">
                  ↓ {stage.rate}%
                </span>
              </div>
              <div className="w-14 md:w-20 flex-shrink-0" data-id="element-1175" />
            </div>}
        </div>)}
    </div>;
}
export function RetentionBreakdownSummer() {
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [adminHourlyRate, setAdminHourlyRate] = useState(40);
  const [leadershipHourlyRate, setLeadershipHourlyRate] = useState(100);
  const [adSpend, setAdSpend] = useState(4000);
  const [cph, setCph] = useState(500);
  const [recruitingMonths, setRecruitingMonths] = useState(3);
  const efficiencyOptions = [{
    label: 'High Efficiency',
    value: 250
  }, {
    label: 'Average Efficiency',
    value: 500
  }, {
    label: 'Low Efficiency',
    value: 750
  }];
  const storedInputs = localStorage.getItem('calculatorInputsSummer');
  const inputs = storedInputs ? JSON.parse(storedInputs) : {};
  const estimatedHires = Math.round((adSpend || 4000) / (cph || 500));
  const totalHires = estimatedHires * recruitingMonths;
  const estimatedLaunched = Math.round(totalHires * 0.7);
  const preInterviewHours = totalHires * 5;
  const onboardingTrainingHours = estimatedLaunched * 5;
  const preInterviewCost = preInterviewHours * adminHourlyRate;
  const onboardingTrainingCost = onboardingTrainingHours * leadershipHourlyRate;
  const handleContinue = () => {
    const updatedInputs = {
      ...inputs,
      adSpend,
      cph,
      recruitingMonths
    };
    localStorage.setItem('calculatorInputsSummer', JSON.stringify(updatedInputs));
    navigate('/contact-summer');
  };
  return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white" data-id="element-1176">
      {/* Header */}
      <header className="border-b-2 border-gray-200 bg-white" data-id="element-1177">
        <div className="max-w-[1400px] mx-auto px-4 py-3 md:py-6 flex items-center justify-between" data-id="element-1178">
          <img src="https://cdn.magicpatterns.com/uploads/jrZpZA6KS7cefrnJNRcqNs/Copy_of_HIRE.png" alt="HIRE" className="h-8 md:h-12 cursor-pointer" onClick={() => navigate('/')} data-id="element-1179" />
          <div className="px-3 py-1 bg-green-500/10 border border-green-500/30 rounded-full text-green-700 text-xs md:text-sm font-semibold" data-id="element-1180">
            SUMMER PROGRAM
          </div>
        </div>
      </header>

      <div className="max-w-[1000px] mx-auto px-4 py-4 md:py-6" data-id="element-1181">
        {/* Hero */}
        <div className="text-center mb-8 md:mb-12" data-id="element-1182">
          <h1 className="text-3xl md:text-5xl font-bold text-navy mb-3 md:mb-4" data-id="element-1183">
            Understanding Your Recruiting Funnel
          </h1>
          <p className="text-base md:text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1184">
            Before we show your results, let's break down the metrics that drive
            your summer ROI
          </p>
        </div>

        {/* Recruiting Assumptions Section */}
        <div className="bg-white border-2 border-gray-200 rounded-lg shadow-lg p-6 md:p-8 mb-8" data-id="element-1185">
          <div className="border-b-2 border-gray-200 pb-3 mb-6" data-id="element-1186">
            <h2 className="text-lg font-bold text-navy" data-id="element-1187">
              Recruiting Assumptions
            </h2>
            <p className="text-sm text-gray-600 mt-1" data-id="element-1188">
              Adjust these values to see how they impact your summer program
            </p>
          </div>

          <div className="space-y-6" data-id="element-1189">
            {/* Months of Recruiting */}
            <div data-id="element-1190">
              <div className="flex items-center justify-between mb-3" data-id="element-1191">
                <label className="text-sm font-semibold text-gray-700" data-id="element-1192">
                  Months of Recruiting Leading Up to Summer
                </label>
                <span className="text-lg font-bold text-navy" data-id="element-1193">
                  {recruitingMonths}{' '}
                  {recruitingMonths === 1 ? 'month' : 'months'}
                </span>
              </div>
              <input type="range" min="1" max="6" step="1" value={recruitingMonths} onChange={e => setRecruitingMonths(parseFloat(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-500" style={{
              background: `linear-gradient(to right, #22c55e 0%, #22c55e ${(recruitingMonths - 1) / (6 - 1) * 100}%, #E5E7EB ${(recruitingMonths - 1) / (6 - 1) * 100}%, #E5E7EB 100%)`
            }} data-id="element-1194" />
              <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-1195">
                <span data-id="element-1196">1 month</span>
                <span data-id="element-1197">6 months</span>
              </div>
              <p className="text-xs text-gray-600 mt-2" data-id="element-1198">
                Total recruiting period before the 5-month summer season begins
              </p>
            </div>

            {/* Monthly Ad Spend */}
            <div data-id="element-1199">
              <div className="flex items-center justify-between mb-3" data-id="element-1200">
                <label className="text-sm font-semibold text-gray-700" data-id="element-1201">
                  Monthly Ad Spend (Indeed/Job Boards)
                </label>
                <span className="text-lg font-bold text-navy" data-id="element-1202">
                  {fmtMoney(adSpend)}
                </span>
              </div>
              <input type="range" min="500" max="10000" step="500" value={adSpend} onChange={e => setAdSpend(parseFloat(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-500" style={{
              background: `linear-gradient(to right, #22c55e 0%, #22c55e ${(adSpend - 500) / (10000 - 500) * 100}%, #E5E7EB ${(adSpend - 500) / (10000 - 500) * 100}%, #E5E7EB 100%)`
            }} data-id="element-1203" />
              <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-1204">
                <span data-id="element-1205">$500</span>
                <span data-id="element-1206">$10,000</span>
              </div>
            </div>

            {/* Cost Per Hire - Button Options */}
            <div data-id="element-1207">
              <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-1208">
                Ad Spend Cost Per Hire
              </label>
              <div className="grid grid-cols-3 gap-3" data-id="element-1209">
                {efficiencyOptions.map(option => <button key={option.value} onClick={() => setCph(option.value)} className={`px-3 py-4 rounded-lg border-2 font-semibold transition-all ${cph === option.value ? 'bg-green-500 border-green-500 text-white shadow-lg' : 'bg-white border-gray-200 text-gray-700 hover:border-green-500 hover:bg-green-50'}`} data-id="element-1210">
                    <div className="text-base md:text-lg font-bold mb-1" data-id="element-1211">
                      {option.label.split(' ')[0]}
                    </div>
                    <div className="text-xs mb-2" data-id="element-1212">
                      {option.label.split(' ')[1]}
                    </div>
                    <div className="text-sm md:text-base font-semibold" data-id="element-1213">
                      {fmtMoney(option.value)}
                    </div>
                  </button>)}
              </div>
            </div>

            {/* Total Hires Display */}
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4" data-id="element-1214">
              <div className="space-y-3" data-id="element-1215">
                <div className="flex items-center justify-between" data-id="element-1216">
                  <label className="text-sm font-semibold text-gray-700" data-id="element-1217">
                    Estimated Monthly Hires
                  </label>
                  <span className="text-xl font-bold text-navy" data-id="element-1218">
                    {estimatedHires}
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-1219">
                  <label className="text-sm font-semibold text-gray-700" data-id="element-1220">
                    Total Hires Over {recruitingMonths}{' '}
                    {recruitingMonths === 1 ? 'Month' : 'Months'}
                  </label>
                  <span className="text-2xl font-bold text-green-600" data-id="element-1221">
                    {totalHires}
                  </span>
                </div>
              </div>
              <p className="text-xs text-gray-600 mt-2" data-id="element-1222">
                {fmtMoney(adSpend)} per month × {recruitingMonths}{' '}
                {recruitingMonths === 1 ? 'month' : 'months'} ÷ {fmtMoney(cph)}{' '}
                per hire = {totalHires} total hires
              </p>
            </div>
          </div>
        </div>

        {/* Main Container */}
        <div className="bg-white border-2 border-gray-200 rounded-lg shadow-lg p-6 md:p-8 space-y-8" data-id="element-1223">
          {/* Overhead Section */}
          <div className="space-y-4" data-id="element-1224">
            <div className="border-b-2 border-gray-200 pb-3" data-id="element-1225">
              <h2 className="text-lg font-bold text-navy" data-id="element-1226">
                Overhead Assumptions
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1227">
              {/* Time Investment */}
              <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-6" data-id="element-1228">
                <h3 className="text-base font-semibold text-gray-700 mb-4" data-id="element-1229">
                  Time Investment
                </h3>
                <div className="space-y-4" data-id="element-1230">
                  <div data-id="element-1231">
                    <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2" data-id="element-1232">
                      Processing, Screening, and Interviewing
                    </label>
                    <div className="text-xl font-bold text-navy" data-id="element-1233">
                      {preInterviewHours} hours
                    </div>
                    <p className="text-xs text-gray-600 mt-1" data-id="element-1234">
                      5 hours × {totalHires} total hires
                    </p>
                  </div>
                  <div data-id="element-1235">
                    <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2" data-id="element-1236">
                      Hiring, Onboarding, and Training
                    </label>
                    <div className="text-xl font-bold text-navy" data-id="element-1237">
                      {onboardingTrainingHours} hours
                    </div>
                    <p className="text-xs text-gray-600 mt-1" data-id="element-1238">
                      5 hours × {estimatedLaunched} launched reps
                    </p>
                  </div>
                </div>
              </div>

              {/* Hourly Rates & Costs */}
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6" data-id="element-1239">
                <h3 className="text-base font-semibold text-gray-700 mb-4" data-id="element-1240">
                  Hourly Rates & Costs
                </h3>
                <div className="space-y-4" data-id="element-1241">
                  <div data-id="element-1242">
                    <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2" data-id="element-1243">
                      Admin Hourly Rate
                    </label>
                    <div className="flex items-center justify-between mb-2" data-id="element-1244">
                      <span className="text-lg font-bold text-navy" data-id="element-1245">
                        {fmtMoney(adminHourlyRate)}/hr
                      </span>
                    </div>
                    <input type="range" min="10" max="75" step="5" value={adminHourlyRate} onChange={e => setAdminHourlyRate(parseFloat(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-500" style={{
                    background: `linear-gradient(to right, #22c55e 0%, #22c55e ${(adminHourlyRate - 10) / (75 - 10) * 100}%, #E5E7EB ${(adminHourlyRate - 10) / (75 - 10) * 100}%, #E5E7EB 100%)`
                  }} data-id="element-1246" />
                    <div className="flex justify-between text-xs text-gray-500 mt-1 mb-2" data-id="element-1247">
                      <span data-id="element-1248">$10</span>
                      <span data-id="element-1249">$75</span>
                    </div>
                    <div className="text-sm font-semibold text-gray-700" data-id="element-1250">
                      Cost: {fmtMoney(preInterviewCost)}
                    </div>
                  </div>
                  <div data-id="element-1251">
                    <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2" data-id="element-1252">
                      Leadership Hourly Rate
                    </label>
                    <div className="flex items-center justify-between mb-2" data-id="element-1253">
                      <span className="text-lg font-bold text-navy" data-id="element-1254">
                        {fmtMoney(leadershipHourlyRate)}/hr
                      </span>
                    </div>
                    <input type="range" min="40" max="200" step="5" value={leadershipHourlyRate} onChange={e => setLeadershipHourlyRate(parseFloat(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-500" style={{
                    background: `linear-gradient(to right, #22c55e 0%, #22c55e ${(leadershipHourlyRate - 40) / (200 - 40) * 100}%, #E5E7EB ${(leadershipHourlyRate - 40) / (200 - 40) * 100}%, #E5E7EB 100%)`
                  }} data-id="element-1255" />
                    <div className="flex justify-between text-xs text-gray-500 mt-1 mb-2" data-id="element-1256">
                      <span data-id="element-1257">$40</span>
                      <span data-id="element-1258">$200</span>
                    </div>
                    <div className="text-sm font-semibold text-gray-700" data-id="element-1259">
                      Cost: {fmtMoney(onboardingTrainingCost)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Recruiting Metrics Section */}
          <div className="space-y-4" data-id="element-1260">
            <div className="border-b-2 border-gray-200 pb-3" data-id="element-1261">
              <h2 className="text-lg font-bold text-navy" data-id="element-1262">
                Recruiting Metrics
              </h2>
              <p className="text-sm text-gray-600 mt-1" data-id="element-1263">
                Standard industry conversion rates
              </p>
            </div>
            <HiringFunnel estimatedHires={totalHires} data-id="element-1264" />
          </div>

          {/* Retention Metrics Section */}
          <div className="space-y-4" data-id="element-1265">
            <div className="border-b-2 border-gray-200 pb-3" data-id="element-1266">
              <h2 className="text-lg font-bold text-navy" data-id="element-1267">Retention Metrics</h2>
              <p className="text-sm text-gray-600 mt-1" data-id="element-1268">
                Adjust the sliders to match your retention expectations
              </p>
            </div>
            <RetentionFunnel estimatedHires={totalHires} data-id="element-1269" />
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 text-center" data-id="element-1270">
          <button onClick={handleContinue} className="inline-flex items-center gap-3 px-8 py-4 bg-green-500 hover:bg-green-600 text-white font-bold text-lg rounded-lg transition-all shadow-xl hover:shadow-green-500/50 hover:scale-105 active:scale-100" data-id="element-1271">
            <span data-id="element-1272">Continue to Full Results</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-1273">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 7l5 5m0 0l-5 5m5-5H6" data-id="element-1274" />
            </svg>
          </button>
        </div>
      </div>
    </div>;
}